// A new account's first run: the intro, setting up the list, a few questions
// that make it theirs, then the tour on the real screens. Where it stands is
// kept on the account (Supabase user metadata, `first_run`), so a second phone
// doesn't start over. Sign-up writes NEW_ACCOUNT; an account without the key
// is from before first runs and never sees one.

export const STAGES = ['intro', 'setup', 'personalize', 'tour', 'done'] as const
export type Stage = (typeof STAGES)[number]
export type ShopsWith = 'me' | 'two' | 'family'
export type Question = 'who' | 'staples' | 'stores' | 'garmin'

export interface FirstRun {
  v: 1
  stage: Stage
  shopsWith?: ShopsWith
  garmin?: boolean
  /** Joined a list by code rather than starting one. */
  joined?: boolean
  /** Tour steps already behind them (lib/tour.ts ids). */
  tourSeen?: string[]
}

export const FIRST_RUN_KEY = 'first_run'
export const NEW_ACCOUNT: FirstRun = { v: 1, stage: 'intro' }

const SHOPS_WITH: readonly ShopsWith[] = ['me', 'two', 'family']

export function readFirstRun(metadata: unknown): FirstRun | null {
  if (!metadata || typeof metadata !== 'object') return null
  const raw = (metadata as Record<string, unknown>)[FIRST_RUN_KEY]
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (r.v !== 1 || !STAGES.includes(r.stage as Stage)) return null
  const out: FirstRun = { v: 1, stage: r.stage as Stage }
  if (SHOPS_WITH.includes(r.shopsWith as ShopsWith)) out.shopsWith = r.shopsWith as ShopsWith
  if (typeof r.garmin === 'boolean') out.garmin = r.garmin
  if (typeof r.joined === 'boolean') out.joined = r.joined
  if (Array.isArray(r.tourSeen)) out.tourSeen = r.tourSeen.filter((s): s is string => typeof s === 'string')
  return out
}

/**
 * The stage to show. Without a list it is the intro or setup, whatever was
 * saved (someone who left their list sets up again); with one, a run that
 * stopped before the questions picks up there.
 */
export function stageFor(firstRun: FirstRun | null, hasHousehold: boolean): Stage {
  if (!hasHousehold) return firstRun?.stage === 'intro' ? 'intro' : 'setup'
  if (!firstRun) return 'done'
  if (firstRun.stage === 'intro' || firstRun.stage === 'setup') return 'personalize'
  return firstRun.stage
}

export function nextStage(stage: Stage): Stage {
  return STAGES[Math.min(STAGES.indexOf(stage) + 1, STAGES.length - 1)]
}

/** Someone who joined finds the list and its stores already there: only the watch is theirs to say. */
export function questionsFor(joined: boolean): Question[] {
  return joined ? ['garmin'] : ['who', 'staples', 'stores', 'garmin']
}
